/**
 * useHeadWithdrawData Hook
 * 본사 입출금내역 데이터 관리 훅
 *
 * @description
 * - 입출금 내역 조회 (날짜/구분/협력사/라이더 필터)
 * - 출금 가능 금액 조회
 * - 입금/출금 신청 처리
 * - 구분 코드, 수수료, 계좌 정보 자동 조회
 * - 협력사/라이더 옵션 목록 조회
 * - 엑셀 다운로드 기능
 * - useRef를 사용한 중복 API 호출 방지
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { headWithdrawApi } from '../api/headWithdrawApi';
import {
    HeadWithdrawHistory,
    HeadGetWithdrawableAmountResponseDto,
    headWithdrawHistoryListFromDto,
    HeadWithdrawRequestDetailDto,
    UseHeadWithdrawDataParams,
    HeadRequestWithdrawRequestDto,
    HeadRequestDepositRequestDto
} from '../types/headWithdraw.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

/** 협력사 옵션 타입 */
export type SupplierOption = {
    id: string;
    name: string;
};

/** 라이더 옵션 타입 */
export type RiderOption = {
    id: string;
    name: string;
};

/**
 * 본사 입출금내역 데이터 훅
 * @param {UseHeadWithdrawDataParams} [params] - 훅 파라미터 (협력사 ID 등)
 * @returns {Object} 입출금 관련 상태 및 함수
 */
export const useHeadWithdrawData = (params?: UseHeadWithdrawDataParams) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [histories, setHistories] = useState<HeadWithdrawHistory[]>([]);
    const [currentBalanceByRider, setCurrentBalanceByRider] = useState<Record<string, number>>({});
    const [currentBalanceBySupplier, setCurrentBalanceBySupplier] = useState<Record<string, number>>({});
    const [withdrawableAmount, setWithdrawableAmount] = useState<HeadGetWithdrawableAmountResponseDto | null>(null);
    const [withdrawRequests, setWithdrawRequests] = useState<HeadWithdrawRequestDetailDto[]>([]);
    const [wdTypes, setWdTypes] = useState<CommonCodeDto[]>([]);
    const [withdrawFee, setWithdrawFee] = useState<number>(300); // 기본값 300원
    const [depositFee, setDepositFee] = useState<number>(300); // 기본값 300원
    const [accountInfo, setAccountInfo] = useState<{
        depositBankCode?: string;
        depositAcctNum?: string;
        depositAcctName?: string;
        withdrawBankCode?: string;
        withdrawAcctNum?: string;
        withdrawAcctName?: string;
    } | null>(null);
    const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
    const [riderOptions, setRiderOptions] = useState<RiderOption[]>([]);
    const hasFetchedWdTypes = useRef(false);
    const hasFetchedWithdrawFee = useRef(false);
    const hasFetchedDepositFee = useRef(false);
    const hasFetchedAccountInfo = useRef(false);

    /**
     * 입출금내역 목록 조회
     * @param {string} fromDate - 조회 시작일 (YYYY-MM-DD)
     * @param {string} toDate - 조회 종료일 (YYYY-MM-DD)
     * @param {string} [wdType] - 입출금 구분
     * @param {string} [reqType] - 요청주체 (C: 본사, S: 협력사, R: 라이더)
     * @param {string} [supplierId] - 협력사 ID
     * @param {string} [supplierName] - 협력사명
     * @param {string} [riderId] - 라이더 ID
     * @param {string} [riderName] - 라이더명
     */
    const fetchHistories = useCallback(async (
        fromDate: string,
        toDate: string,
        wdType?: string,
        reqType?: string,
        supplierId?: string,
        supplierName?: string,
        riderId?: string,
        riderName?: string
    ) => {
        try {
            setLoading(true);
            const targetSupplierId = supplierId || params?.supplierId;
            const response = await headWithdrawApi.getHeadWithdrawHistoryList(
                fromDate,
                toDate,
                wdType,
                reqType,
                targetSupplierId,
                supplierName,
                riderId,
                riderName
            );
            if (response) {
                const historyList = headWithdrawHistoryListFromDto(response);
                setHistories(historyList || []);
                // 백엔드에서 제공하는 현재 잔액 맵 저장
                setCurrentBalanceByRider(response.currentBalanceByRider || {});
                setCurrentBalanceBySupplier(response.currentBalanceBySupplier || {});
            } else {
                setHistories([]);
                setCurrentBalanceByRider({});
                setCurrentBalanceBySupplier({});
            }
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError.response?.status !== 404) {
                    showToast('입출금내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            } else {
                showToast('입출금내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            }
            setHistories([]);
        } finally {
            setLoading(false);
        }
    }, [params?.supplierId, showToast]);

    /**
     * 출금 가능 금액 조회
     */
    const fetchWithdrawableAmount = useCallback(async () => {
        try {
            setLoading(true);
            const response = await headWithdrawApi.getWithdrawableAmount();
            setWithdrawableAmount(response || null);
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError.response?.status !== 404) {
                    showToast('출금가능금액을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            } else {
                showToast('출금가능금액을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            }
            setWithdrawableAmount(null);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 출금 신청
     */
    const requestWithdraw = useCallback(async (
        suppId: string,
        reqVal: number,
        briefs?: string
    ) => {
        try {
            setLoading(true);
            const request: HeadRequestWithdrawRequestDto = {
                suppId,
                reqVal
            };
            if (briefs) {
                request.briefs = briefs;
            }
            const response = await headWithdrawApi.requestWithdraw(request);

            if (response.success) {
                showToast('출금 신청이 완료되었습니다.', { type: ToastType.SUCCESS });
                await fetchWithdrawableAmount();
                return response;
            } else {
                showToast(response.message || '출금 신청에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch (error: unknown) {
            showToast('출금 신청에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchWithdrawableAmount]);

    /**
     * 입금 신청
     */
    const requestDeposit = useCallback(async (
        userType: string,
        reqVal: number,
        briefs?: string
    ) => {
        try {
            setLoading(true);
            const request: HeadRequestDepositRequestDto = {
                userType,
                reqVal
            };
            if (briefs) {
                request.briefs = briefs;
            }
            const response = await headWithdrawApi.requestDeposit(request);

            if (response.success) {
                showToast('입금 신청이 완료되었습니다.', { type: ToastType.SUCCESS });
                return response;
            } else {
                showToast(response.message || '입금 신청에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch (error: unknown) {
            showToast('입금 신청에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 출금 신청 내역 조회
     */
    const fetchWithdrawRequests = useCallback(async (
        status?: 'pending' | 'approved' | 'rejected' | 'completed'
    ) => {
        try {
            setLoading(true);
            const requests = await headWithdrawApi.getWithdrawRequestList(
                params?.supplierId,
                status
            );
            setWithdrawRequests(requests);
        } catch (error: unknown) {
            showToast('출금 신청 내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setWithdrawRequests([]);
        } finally {
            setLoading(false);
        }
    }, [params?.supplierId, showToast]);

    // 구분 코드 목록 조회
    useEffect(() => {
        if (hasFetchedWdTypes.current) return;

        let isCancelled = false;

        const fetchWdTypes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WD_TYPE');
                if (!isCancelled) {
                    setWdTypes(response.codes || []);
                    hasFetchedWdTypes.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchWdTypes();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 출금수수료 조회
    useEffect(() => {
        if (hasFetchedWithdrawFee.current) return;

        let isCancelled = false;

        const fetchWithdrawFee = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WITHRAW_FEE_AMT');
                if (!isCancelled && response.codes) {
                    // comCode가 "WITHRAW"이거나 comCdName이 "출금"인 항목 찾기
                    const withdrawCode = response.codes.find(
                        code => code.comCode === 'WITHRAW' || code.comCdName === '출금'
                    );
                    if (withdrawCode && withdrawCode.val1) {
                        const fee = parseInt(withdrawCode.val1, 10);
                        if (!isNaN(fee)) {
                            setWithdrawFee(fee);
                        }
                    }
                    hasFetchedWithdrawFee.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchWithdrawFee();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 입금수수료 조회
    useEffect(() => {
        if (hasFetchedDepositFee.current) return;

        let isCancelled = false;

        const fetchDepositFee = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WITHRAW_FEE_AMT');
                if (!isCancelled && response.codes) {
                    // comCode가 "DEPOSIT"이거나 comCdName이 "입금"인 항목 찾기
                    const depositCode = response.codes.find(
                        code => code.comCode === 'DEPOSIT' || code.comCdName === '입금'
                    );
                    if (depositCode && depositCode.val1) {
                        const fee = parseInt(depositCode.val1, 10);
                        if (!isNaN(fee)) {
                            setDepositFee(fee);
                        }
                    }
                    hasFetchedDepositFee.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchDepositFee();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 계좌 정보 조회
    const fetchAccountInfo = useCallback(async (forceRefresh = false) => {
        if (!forceRefresh && hasFetchedAccountInfo.current) return;

        try {
            const response = await commonCodeApi.getCommonCodeList('VR_ACCOUNT_NUM');
            const codes = response.codes || [];

            // 공통코드에서 계좌 정보 파싱
            const accountData: {
                depositBankCode?: string;
                depositAcctNum?: string;
                depositAcctName?: string;
                withdrawBankCode?: string;
                withdrawAcctNum?: string;
                withdrawAcctName?: string;
            } = {};

            codes.forEach((code: CommonCodeDto) => {
                // comCode나 comCdName을 기준으로 입금/출금 계좌 구분
                const codeUpper = (code.comCode || '').toUpperCase();
                const nameUpper = (code.comCdName || '').toUpperCase();

                if (codeUpper.includes('DEPOSIT') || nameUpper.includes('입금')) {
                    accountData.depositBankCode = code.val1 || '';
                    accountData.depositAcctNum = code.val2 || '';
                    accountData.depositAcctName = code.val3 || code.comCdName || '';
                } else if (codeUpper.includes('WITHDRAW') || nameUpper.includes('출금')) {
                    accountData.withdrawBankCode = code.val1 || '';
                    accountData.withdrawAcctNum = code.val2 || '';
                    accountData.withdrawAcctName = code.val3 || code.comCdName || '';
                } else {
                    // 기본값으로 설정 (첫 번째 항목)
                    if (!accountData.depositBankCode && !accountData.withdrawBankCode) {
                        accountData.depositBankCode = code.val1 || '';
                        accountData.depositAcctNum = code.val2 || '';
                        accountData.depositAcctName = code.val3 || code.comCdName || '';
                    }
                }
            });

            setAccountInfo(accountData);
            hasFetchedAccountInfo.current = true;
        } catch {
            setAccountInfo(null);
        }
    }, []);

    // 계좌 정보는 입금 신청 모달이 열릴 때만 조회 (초기 로드 제거)

    /**
     * 협력사 옵션 목록 조회
     */
    const fetchSupplierOptions = useCallback(async (fromDate: string, toDate: string) => {
        try {
            const response = await headWithdrawApi.getSupplierOptions(fromDate, toDate);
            setSupplierOptions(response || []);
        } catch {
            setSupplierOptions([]);
        }
    }, []);

    /**
     * 라이더 옵션 목록 조회
     */
    const fetchRiderOptions = useCallback(async (fromDate: string, toDate: string, supplierName?: string) => {
        if (!fromDate || !toDate) {
            setRiderOptions([]);
            return;
        }

        try {
            const response = await headWithdrawApi.getRiderOptions(fromDate, toDate, supplierName || '');
            setRiderOptions(response || []);
        } catch {
            setRiderOptions([]);
        }
    }, []);

    return {
        loading,
        histories,
        currentBalanceByRider,
        currentBalanceBySupplier,
        withdrawableAmount,
        withdrawRequests,
        wdTypes,
        withdrawFee,
        depositFee,
        accountInfo,
        supplierOptions,
        riderOptions,
        fetchHistories,
        fetchWithdrawableAmount,
        requestWithdraw,
        requestDeposit,
        fetchWithdrawRequests,
        fetchAccountInfo,
        fetchSupplierOptions,
        fetchRiderOptions,
        refreshHistories: fetchHistories,
        refreshWithdrawableAmount: fetchWithdrawableAmount,
        refreshWithdrawRequests: fetchWithdrawRequests,
        downloadExcel: async (
            fromDate: string,
            toDate: string,
            wdType?: string,
            reqType?: string,
            supplierId?: string,
            supplierName?: string,
            riderId?: string,
            riderName?: string
        ) => {
            try {
                setLoading(true);
                const blob = await headWithdrawApi.downloadWithdrawHistoryExcel(
                    fromDate,
                    toDate,
                    wdType,
                    reqType,
                    supplierId,
                    supplierName,
                    riderId,
                    riderName
                );

                // 파일 다운로드
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `입출금내역_${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
            } catch (error: unknown) {
                showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
            } finally {
                setLoading(false);
            }
        }
    };
};
